import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBrandLinkedin,
  IconBuildingStore,
  IconExternalLink,
  IconMail,
  IconPlus,
} from '@tabler/icons-react';
import { suggestChannel } from '../channelSuggester';

const STRONG_FIT_THRESHOLD = 80;

function fitLabel(score) {
  const v = Number(score || 0);
  if (v >= STRONG_FIT_THRESHOLD) return 'STRONG';
  if (v >= 60) return 'GOOD';
  if (v >= 40) return 'FAIR';
  return 'LOW';
}

function fitColor(score) {
  const v = Number(score || 0);
  if (v >= STRONG_FIT_THRESHOLD) return 'var(--mantine-color-sage-6)';
  if (v >= 60) return 'var(--mantine-color-sage-4)';
  if (v >= 40) return 'var(--aviary-muted)';
  return 'var(--aviary-disabled)';
}

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function subtitleFor(prospect, isCoachFlow) {
  const title = String(prospect.title || '').trim();
  const company = String(prospect.company || '').trim();
  if (isCoachFlow) {
    if (title && company) return `${title} · ${company}`;
    return title || company || '';
  }
  // Small biz: business type + address fragment
  const city = String(prospect.city || '').trim() || String(prospect.address || '').split(',')[0].trim();
  return [company, city].filter(Boolean).join(' · ');
}

function locationLine(prospect) {
  const city = String(prospect.city || '').trim();
  const country = String(prospect.country || '').trim();
  if (city && country) return `${city}, ${country}`;
  return city || country || String(prospect.address || '').trim();
}

function ProspectRail({
  prospect,
  isCoachFlow,
  isActive,
  isChecked,
  isDisabled,
  onSelect,
  onToggleCheck,
}) {
  const subtitle = subtitleFor(prospect, isCoachFlow);
  const score = Math.round(Number(prospect.fitScore ?? prospect.score ?? 0));
  const inPipeline = Boolean(prospect.existing);

  return (
    <UnstyledButton
      onClick={() => onSelect(prospect)}
      style={{
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        padding: '10px 12px',
        borderRadius: 10,
        border: isActive
          ? '2px solid var(--mantine-color-sage-6)'
          : inPipeline
            ? '1px dashed var(--aviary-disabled)'
            : '1px solid var(--aviary-disabled)',
        background: isActive ? 'var(--mantine-color-sage-0)' : 'var(--aviary-surface)',
        opacity: inPipeline ? 0.55 : 1,
      }}
    >
      <Group gap={10} wrap="nowrap" align="center">
        <Checkbox
          checked={isChecked}
          disabled={isDisabled}
          color="sage"
          size="sm"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            if (!isDisabled) onToggleCheck(prospect);
          }}
        />
        <Avatar
          src={prospect.photoUrl || null}
          size={36}
          radius="xl"
          color="sage"
        >
          {isCoachFlow ? initialsFor(prospect.name) : <IconBuildingStore size={18} />}
        </Avatar>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)', lineHeight: 1.2 }} truncate>
            {prospect.name || '(no name)'}
          </Text>
          <Text size="xs" style={{ color: 'var(--aviary-muted)', lineHeight: 1.2 }} truncate>
            {inPipeline ? 'already in pipeline' : (subtitle || '—')}
          </Text>
        </Stack>
        {!inPipeline && score > 0 && (
          <Text size="sm" fw={700} style={{ color: fitColor(score), flexShrink: 0 }}>
            {score}
          </Text>
        )}
      </Group>
    </UnstyledButton>
  );
}

function FitCircle({ score }) {
  const v = Math.round(Number(score || 0));
  const color = fitColor(v);
  return (
    <Stack align="center" gap={0} style={{ flexShrink: 0 }}>
      <Box
        style={{
          width: 68,
          height: 68,
          borderRadius: '50%',
          border: `3px solid ${color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text fw={700} size="xl" style={{ color: 'var(--aviary-ink)', lineHeight: 1 }}>
          {v}
        </Text>
      </Box>
      <Text size="xs" fw={600} mt={4} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.5 }}>
        {fitLabel(v)}
      </Text>
    </Stack>
  );
}

function ActionChip({ Icon, label, href, onClick, disabled }) {
  if (disabled) return null;
  const content = (
    <Group gap={6} align="center" wrap="nowrap">
      <Icon size={14} />
      <Text size="xs" fw={500}>{label}</Text>
    </Group>
  );
  const style = {
    padding: '6px 12px',
    borderRadius: 20,
    border: '1px solid var(--aviary-disabled)',
    color: 'var(--aviary-ink)',
    background: 'var(--aviary-surface)',
    display: 'inline-block',
  };
  if (href) {
    return (
      <UnstyledButton component="a" href={href} target="_blank" rel="noopener noreferrer" style={style}>
        {content}
      </UnstyledButton>
    );
  }
  return (
    <UnstyledButton onClick={onClick} style={style}>
      {content}
    </UnstyledButton>
  );
}

// Collapsed view = first ~3 sentences. Expanded view = full raw note (the coach-strategy
// prompt already caps at 80 words for new notes; older verbose ones show in full on expand).
function shortWhy(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { short: '', full: '', truncated: false };
  const sentences = trimmed.match(/[^.!?]+[.!?]+/g) || [trimmed];
  const short3 = sentences.slice(0, 3).join(' ').trim();
  const truncated = short3.length < trimmed.length;
  return { short: short3 || trimmed, full: trimmed, truncated };
}

function firstNameOf(name) {
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  return first;
}

// Extract "atlassian.com" from any of: https://www.atlassian.com/careers, atlassian.com/, www.atlassian.com
function domainOf(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const withScheme = raw.startsWith('http') ? raw : `https://${raw}`;
  try {
    const host = new URL(withScheme).hostname.replace(/^www\./, '');
    return host || raw;
  } catch (_err) {
    return raw.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

function DetailPanel({
  prospect,
  isCoachFlow,
  draft,
  onDraftChange,
}) {
  const [expanded, setExpanded] = useState(false);
  const notesValue = draft.manualCoachNotes !== undefined
    ? draft.manualCoachNotes
    : (prospect.manualCoachNotes || '');

  const whyRaw = String(prospect.coachNote || prospect.fitReason || '').trim();
  const { short, full, truncated } = shortWhy(whyRaw);
  const score = Math.round(Number(prospect.fitScore ?? prospect.score ?? 0));
  const subtitle = subtitleFor(prospect, isCoachFlow);
  const location = locationLine(prospect);
  const email = String(prospect.bestEmail || prospect.email || '').trim();
  const linkedin = String(prospect.linkedinUrl || prospect.contactEnrichment?.socials?.linkedin || '').trim();
  const website = String(prospect.website || '').trim();

  return (
    <Stack gap="lg">
      <Group align="flex-start" wrap="nowrap" gap="md">
        <Avatar
          src={prospect.photoUrl || null}
          size={64}
          radius="xl"
          color="sage"
        >
          {isCoachFlow ? initialsFor(prospect.name) : <IconBuildingStore size={28} />}
        </Avatar>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="lg" style={{ color: 'var(--aviary-ink)', lineHeight: 1.2 }}>
            {prospect.name || '(no name)'}
          </Text>
          {subtitle && (
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
              {subtitle}
            </Text>
          )}
          {location && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{location}</Text>
          )}
          <Group gap={8} mt={6} wrap="wrap">
            {isCoachFlow && linkedin && (
              <ActionChip Icon={IconBrandLinkedin} label="LinkedIn" href={linkedin} />
            )}
            {email && (
              <ActionChip Icon={IconMail} label="Email" href={`mailto:${email}`} />
            )}
            {website && (
              <ActionChip
                Icon={IconExternalLink}
                label={domainOf(website)}
                href={website.startsWith('http') ? website : `https://${website}`}
              />
            )}
          </Group>
        </Stack>
        {score > 0 && <FitCircle score={score} />}
      </Group>

      {short && (
        <Stack gap={6}>
          <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
            {isCoachFlow
              ? `Why ${firstNameOf(prospect.name) || 'this person'} is a good fit`
              : `Why ${prospect.company || prospect.name || 'this business'} is a good fit`}
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55 }}>
            {expanded ? full : short}
          </Text>
          {truncated && (
            <UnstyledButton onClick={() => setExpanded((v) => !v)}>
              <Text size="xs" c="sage" fw={600}>
                {expanded ? 'show less' : 'show more'}
              </Text>
            </UnstyledButton>
          )}
        </Stack>
      )}

      <Stack gap={6}>
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
          NOTES (optional)
        </Text>
        <Textarea
          value={notesValue}
          onChange={(e) => onDraftChange(prospect.externalId, { manualCoachNotes: e.currentTarget.value })}
          autosize
          minRows={2}
          placeholder="Anything the agent should know before reaching out…"
          styles={{
            input: {
              background: 'var(--aviary-surface)',
              border: '1px dashed var(--aviary-disabled)',
              borderRadius: 8,
              fontSize: 13.5,
              lineHeight: 1.5,
              color: 'var(--aviary-ink)',
            },
          }}
        />
      </Stack>
    </Stack>
  );
}

export default function ProspectsScreen({
  flow,
  proposal,
  manualProspects,
  selectedProspectIds,
  onSelectedIdsChange,
  onToggleProspect,
  onAddManual,
  principalName = '',  // eslint-disable-line no-unused-vars
  prospectDrafts = {},
  onProspectDraftChange = () => {},
  onProceedToLaunch,
}) {
  const discovered = proposal?.prospects || [];
  const warning = proposal?.warning;
  const hiddenUnreachable = Number(proposal?.hiddenUnreachable || 0);
  const audienceNoun = flow?.audience_noun || 'people';
  const isCoachFlow = flow?.search_source === 'people_search';

  // Hide prospects that are already in the principal's pipeline — they clutter the rail
  // and can't be re-imported anyway. A single-line footer note reports how many were hidden.
  const allProspects = useMemo(
    () => [...discovered, ...manualProspects].filter((p) => !p.existing),
    [discovered, manualProspects]
  );
  const alreadyInPipelineCount = useMemo(
    () => discovered.filter((p) => p.existing).length,
    [discovered]
  );
  const selectable = allProspects.filter((p) => p.externalId);

  // Top-5 "take these and go" split — always shown at the top of the rail. The rest are
  // hidden behind a "N more" collapsed row until the coach opts in with "show all".
  const TOP_N = 5;
  const topProspects = useMemo(() => allProspects.slice(0, TOP_N), [allProspects]);
  const restProspects = useMemo(() => allProspects.slice(TOP_N), [allProspects]);

  const [showAll, setShowAll] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const visibleProspects = showAll ? allProspects : topProspects;

  const [activeId, setActiveId] = useState(() => {
    const first = allProspects.find((p) => !p.existing);
    return first ? String(first.externalId) : (allProspects[0] ? String(allProspects[0].externalId) : null);
  });

  useEffect(() => {
    if (activeId && allProspects.some((p) => String(p.externalId) === activeId)) return;
    const first = allProspects.find((p) => !p.existing);
    if (first) setActiveId(String(first.externalId));
    else if (allProspects[0]) setActiveId(String(allProspects[0].externalId));
  }, [allProspects, activeId]);

  const activeProspect = allProspects.find((p) => String(p.externalId) === activeId) || allProspects[0] || null;

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', title: '', email: '', phone: '' });
  const [formError, setFormError] = useState('');

  function commitSelection(nextSet) {
    onSelectedIdsChange(nextSet);
  }

  function deselectAll() {
    commitSelection(new Set());
  }

  function openModal() {
    setForm({ name: '', company: '', title: '', email: '', phone: '' });
    setFormError('');
    setModalOpen(true);
  }

  function saveManual() {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name) {
      setFormError('Name is required.');
      return;
    }
    if (!email && !phone) {
      setFormError('Add an email or a phone number.');
      return;
    }
    const draft = {
      name,
      company: form.company.trim(),
      title: form.title.trim(),
      email,
      phone,
      source: 'manual',
    };
    const suggested = suggestChannel(draft, flow?.search_source || 'people_search');
    suggested.reason = 'Manually added';
    const prospect = {
      ...draft,
      externalId: `manual-${crypto.randomUUID()}`,
      suggested,
      existing: false,
    };
    onAddManual(prospect);
    setActiveId(prospect.externalId);
    setModalOpen(false);
  }

  // Header count reflects what's visible (excludes prospects already in pipeline).
  const visibleCount = allProspects.length;
  const header = visibleCount === 0 && alreadyInPipelineCount === 0
    ? `I could not find any ${audienceNoun} automatically.`
    : visibleCount === 0
      ? `All ${alreadyInPipelineCount} matches are already in your pipeline.`
      : `I found ${visibleCount}. All ticked — untick any you don't want.`;

  const activeDraft = activeProspect ? (prospectDrafts[String(activeProspect.externalId)] || {}) : {};
  const selectedCount = selectedProspectIds.size;

  // Show the "all preselected" banner when the coach hasn't touched selection yet
  // (i.e. still has every selectable prospect ticked) and hasn't dismissed it.
  const preselectionIntact = useMemo(() => {
    const selectableIds = allProspects.filter((p) => !p.existing).map((p) => String(p.externalId));
    if (selectedCount !== selectableIds.length) return false;
    return selectableIds.every((id) => selectedProspectIds.has(id));
  }, [selectedCount, allProspects, selectedProspectIds]);
  const showBanner = !bannerDismissed && preselectionIntact && visibleCount > 0;

  const restCount = restProspects.length;

  return (
    <Stack gap="md">
      <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 26, lineHeight: 1.25 }}>
        {header}
      </Text>

      {discovered.length === 0 && manualProspects.length === 0 && warning && (
        <Alert color="yellow" variant="light" title="Couldn't reach the search source">
          Add some prospects yourself to continue.
        </Alert>
      )}

      {showBanner && (
        <Paper
          p="md"
          radius="md"
          style={{
            background: 'var(--mantine-color-sage-0)',
            border: '1px solid var(--mantine-color-sage-3)',
          }}
        >
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.5 }}>
              All {selectedCount} are ticked. Launch to them today, add more tomorrow.
            </Text>
            {onProceedToLaunch && (
              <Button
                color="sage"
                size="sm"
                onClick={onProceedToLaunch}
                styles={{ root: { flexShrink: 0 } }}
              >
                Take all {selectedCount} →
              </Button>
            )}
          </Group>
        </Paper>
      )}

      {allProspects.length > 0 && (
        <Group align="flex-start" wrap="nowrap" gap="md">
          {/* LEFT RAIL */}
          <Stack gap="xs" style={{ width: 380, flexShrink: 0 }}>
            <Group justify="space-between" align="center">
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>sorted by fit</Text>
              <Group gap="xs">
                {restCount > 0 && (
                  <UnstyledButton onClick={() => setShowAll((v) => !v)}>
                    <Text size="xs" c="sage">
                      {showAll ? `show top ${TOP_N}` : `show all ${allProspects.length}`}
                    </Text>
                  </UnstyledButton>
                )}
                {selectedCount > 0 && (
                  <>
                    <Text size="xs" style={{ color: 'var(--aviary-disabled)' }}>·</Text>
                    <UnstyledButton onClick={deselectAll}>
                      <Text size="xs" c="sage">deselect all</Text>
                    </UnstyledButton>
                  </>
                )}
              </Group>
            </Group>

            <ScrollArea.Autosize mah={640} type="auto" scrollbars="y" offsetScrollbars>
              <Stack gap={6} style={{ minWidth: 0 }}>
                {visibleProspects.map((prospect) => {
                  const id = String(prospect.externalId);
                  const disabled = Boolean(prospect.existing);
                  const isChecked = disabled ? false : selectedProspectIds.has(id);
                  return (
                    <ProspectRail
                      key={id}
                      prospect={prospect}
                      isCoachFlow={isCoachFlow}
                      isActive={activeId === id}
                      isChecked={isChecked}
                      isDisabled={disabled}
                      onSelect={(p) => setActiveId(String(p.externalId))}
                      onToggleCheck={(p) => onToggleProspect(p.externalId)}
                    />
                  );
                })}
                {!showAll && restCount > 0 && (
                  <UnstyledButton
                    onClick={() => setShowAll(true)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '14px 12px',
                      borderRadius: 10,
                      border: '1px dashed var(--aviary-disabled)',
                      background: 'transparent',
                      textAlign: 'center',
                    }}
                  >
                    <Text size="sm" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                      {restCount} more, untouched
                    </Text>
                  </UnstyledButton>
                )}
              </Stack>
            </ScrollArea.Autosize>

            <Button
              variant="subtle"
              color="sage"
              leftSection={<IconPlus size={14} />}
              onClick={openModal}
              size="xs"
              styles={{ root: { justifyContent: 'flex-start' } }}
            >
              Add a prospect manually
            </Button>

            <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
              tick as you scan · {selectable.length} rows to review
              {alreadyInPipelineCount > 0 && ` · hid ${alreadyInPipelineCount} already in your pipeline`}
            </Text>
          </Stack>

          {/* DETAIL PANEL */}
          <Paper p="lg" radius="md" withBorder style={{ flex: 1, background: 'var(--aviary-surface)', minWidth: 0 }}>
            {activeProspect ? (
              <DetailPanel
                prospect={activeProspect}
                isCoachFlow={isCoachFlow}
                draft={activeDraft}
                onDraftChange={onProspectDraftChange}
              />
            ) : (
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                Pick a prospect on the left to see details.
              </Text>
            )}
          </Paper>
        </Group>
      )}

      {hiddenUnreachable > 0 && (
        <Text size="xs" c="dimmed">
          Hid {hiddenUnreachable} {isCoachFlow ? 'people' : 'businesses'} with no reachable contact for this channel.
        </Text>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add a prospect"
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
          />
          <TextInput
            label="Company"
            value={form.company}
            onChange={(e) => setForm((f) => ({ ...f, company: e.currentTarget.value }))}
          />
          <TextInput
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.currentTarget.value }))}
          />
          <TextInput
            label="Email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.currentTarget.value }))}
          />
          <TextInput
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.currentTarget.value }))}
          />
          {formError && <Text size="sm" c="red">{formError}</Text>}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="sage" onClick={saveManual}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
